/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, ButtonBase, Link, SelectChangeEvent, Stack } from '@mui/material';
import Typography from '../../../../components/Typography';
import { Button } from '../../../../components/Button';
import MaterialSymbol from '../../../../components/MaterialSymbol';
import { colors } from '@farmlands/mui-theme';
import { DetailsApiResponse } from '../../../products/storage';
import { useCampaignContext } from './CampaignContext';
import PlaceholderImage from '../../../../assets/images/image-placeholder.png';
import React, { useEffect, useState } from 'react';
import {
  FreightCalculateRequest,
  FreightCalculateResponse,
  FreightCharge,
  FreightRange,
  SaleAgreementPromo,
  SaleAgreementPromoProductSelection,
  SalesAgreementPromoProductSelectionVariant,
  SiteInfo,
} from './types';
import { formatPrice, generateImageUrl } from '../../../../util/helpers';
import { ModalActions } from '../../../../components/Modal';
import {
  getSites,
  isLocationInSameIsland,
  selectProductDeliveryMonthPlan,
  transfromProductSelections,
} from './utils';
import DropdownList from '../../../../components/Dropdownlist';
import { useMutation, useQuery } from '@tanstack/react-query';
import { freightCalculateApi } from './api';
import TabHeader from './TabHeader';
import { SelectSiteView } from './PickupSite';
import { AxiosError, AxiosResponse } from 'axios';
import { UNIT_MAP_LONG } from '../../../../util/consts';
import ProductDetailModal, { useProductDetailModalControl } from './ProductDetailModal';
import ImageWithPlaceholder from '../../../../components/ImageWithPlaceholder';
import { useSelector } from 'react-redux';
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage';
import { useBranch } from '../../../../util/useBranch';
import apiClient from '../../../../services/api-client';

interface SearchAddressTabProps {
  config: SaleAgreementPromo;
  campaignId: string;
}

enum FreightCalculateApiErrorCode {
  InvalidAddress = 'urn:errors:invalid-address',
  MapboxFailed = 'urn:errors:mapbox-call-failed',
  InvalidSite = 'urn:errors:invalid-site',
  InvalidAgreement = 'urn:errors:invalid-agreement',
}

type FreightCalculateApiError = AxiosError<{
  status: number;
  type: FreightCalculateApiErrorCode;
}>;

const SelectProductTab: React.FC<SearchAddressTabProps> = ({ config, campaignId }) => {
  const { state, setState } = useCampaignContext();
  const [error, setError] = React.useState<undefined | string>();
  const isFreightInclusive = !config.freightRequired;
  const { setOpen, setClose, isOpen, productId } = useProductDetailModalControl();

  const freightCalculateMutation = useMutation<
    AxiosResponse<FreightCalculateResponse>,
    FreightCalculateApiError,
    FreightCalculateRequest
  >({
    mutationFn: async (payload: FreightCalculateRequest) => {
      return await freightCalculateApi(payload);
    },
    onSuccess: (res) => {
      setState({ freightCharge: res.data.unitPrice });
      setError(undefined);
    },
    onError: (err) => {
      if (err instanceof AxiosError) {
        switch (err.response?.data.type) {
          case FreightCalculateApiErrorCode.MapboxFailed:
            setState({ freightCharge: null });
            break;
          case FreightCalculateApiErrorCode.InvalidAddress:
            setError('Oops, delivery address and site must be same island');
            break;
          default:
            setError('Something went wrong');
        }
      }
    },
  });

  // calculate freight cost on component mounted only
  React.useEffect(() => {
    if (state.freightRangeSelected !== undefined || isFreightInclusive) {
      return;
    }

    if (state.siteSelected !== undefined && state.addressSelected !== undefined) {
      freightCalculateMutation.mutate({
        templateAgreementId: campaignId,
        siteId: state.siteSelected.siteId,
        fullAddress: addressSelectedText,
      });
    }
  }, []);

  const availableSites = React.useMemo(() => {
    const sites = getSites(config.products);

    if (!state.addressSelected) {
      return sites;
    }

    const postcode =
      'Postcode' in state.addressSelected
        ? state.addressSelected.Postcode
        : state.addressSelected?.zipCode;

    if (Number(postcode) <= 0) {
      return sites;
    }

    return sites.filter((site) => {
      return isLocationInSameIsland(Number(site.sitePostcode), Number(postcode));
    });
  }, [config.products, state.addressSelected]);

  useEffect(() => {
    if (state.siteSelected && availableSites.length > 0) {
      const available = availableSites.find((e) =>
        isLocationInSameIsland(Number(e.sitePostcode), Number(state.siteSelected?.sitePostcode)),
      );

      if (!available) {
        setState({
          siteSelected: undefined,
        });
      }
    }
  }, [availableSites]);

  const siteSelectedProducts = React.useMemo(() => {
    let productSelections: SaleAgreementPromoProductSelection[] = [];

    if (state.siteSelected?.siteId) {
      productSelections = transfromProductSelections(
        config.products.filter((p) => {
          return p.siteId === state.siteSelected?.siteId;
        }),
      );
    } else if (config.hideSite === true) {
      // filter product based on site selected
      productSelections = transfromProductSelections(config.products);
    } else {
      productSelections = transfromProductSelections(
        config.products.filter((p) => {
          return p.siteId === config.defaultSiteId;
        }),
      );
    }

    return productSelections;
  }, [state.siteSelected, config.products, config.hideSite]);

  const addressSelectedText = React.useMemo(() => {
    let fullAddressText = '';

    if (state.addressSelected === undefined) {
      return fullAddressText;
    }

    if ('UniqueId' in state.addressSelected) {
      fullAddressText = state.addressSelected.FullAddress;
    } else {
      fullAddressText = state.addressSelected.fullAddress;
    }

    return fullAddressText;
  }, [state.addressSelected]);

  const [productError, setProductError] = React.useState<undefined | string>();
  useEffect(() => {
    if (state.productSelected.length > 0) {
      setProductError(undefined);
    }
  }, [state.productSelected.length]);

  function handleSubmitNextStep() {
    const hasProductSelected = state.productSelected.length > 0;
    const freightCostIsCalculated = typeof state.freightCharge === 'number';
    if (!hasProductSelected) {
      setProductError('Please select at least one product');
      return;
    }

    if (hasProductSelected && (isFreightInclusive || freightCostIsCalculated)) {
      setState({
        tabIndex: state.tabIndex + 1,
      });
    }
  }

  function handleSiteSelect(site: SiteInfo) {
    setState({
      siteSelected: site,
      productSelected: [],
      selectDistance: false,
      freightCharge: undefined, // reset for re-caculate
      freightRangeSelected: undefined, // reset for re-caculate
    });

    if (isFreightInclusive) {
      return;
    }

    freightCalculateMutation.mutate({
      templateAgreementId: campaignId,
      siteId: site.siteId,
      fullAddress: addressSelectedText,
    });
  }

  function handleOnFreightRangeSelect(freightRange: FreightRange) {
    if (!state.siteSelected) {
      console.error('Error: siteId is undefined');
      return;
    }

    freightCalculateMutation.mutate({
      templateAgreementId: campaignId,
      siteId: state.siteSelected.siteId,
      freightRange,
    });

    setState({
      selectDistance: false,
      freightRangeSelected: freightRange,
    });
  }

  const handleProductClick = (productId: number) => {
    setOpen(productId);
  };

  if (state.siteSelected === undefined && !config.hideSite) {
    return <SelectSiteView sites={availableSites} onSiteSelect={handleSiteSelect} />;
  }

  if (state.selectDistance) {
    return (
      <SelectDistanceView
        freightRanges={config.freightRanges}
        onFreightRangeSelect={handleOnFreightRangeSelect}
      />
    );
  }

  return (
    <Stack direction='column' pb='100px'>
      {error ? (
        <Typography variant='caption' color='error500'>
          {error}
        </Typography>
      ) : null}
      <SelectProductView
        config={config}
        products={siteSelectedProducts}
        isFreightInclusive={isFreightInclusive}
        onProductClick={handleProductClick}
      />
      <ModalActions showDivider>
        {productError && (
          <Typography variant='body2' color='error500' mr={'auto'}>
            {productError}
          </Typography>
        )}
        <Button
          key='next-step-btn'
          variant='contained'
          onClick={handleSubmitNextStep}
          // data-testid={TestIds.feat.product.details.orderButton}
          sx={(theme) => ({
            [theme.breakpoints.down('sm')]: {
              width: '100%',
            },
          })}
        >
          Next Step
        </Button>
      </ModalActions>
      <ProductDetailModal open={isOpen} handleClose={setClose} productId={productId} />
    </Stack>
  );
};

type CampaignProductProps = {
  detail: SaleAgreementPromoProductSelection;
  productIds: number[];
  onSelect: (select: boolean, product: SalesAgreementPromoProductSelectionVariant) => void;
  onSelectDistance: () => void;
  onClick: SelectProductViewProps['onProductClick'];
  isFreightInclusive: boolean;
  freightCharge: FreightCharge;
  canEditPrice?: boolean;
};

const CampaignProduct: React.FC<CampaignProductProps> = ({
  detail,
  productIds,
  onSelect,
  onSelectDistance,
  onClick,
  isFreightInclusive,
  freightCharge,
  canEditPrice,
}) => {
  const { description, unit, productId, variants, itemNumber } = detail;

  const account = useSelector(selectCurrentFarmerAccount);
  const filteredVariants = variants?.filter(
    (variant) => Boolean(variant.colour) || Boolean(variant.size),
  );

  const [isExpanded, setIsExpanded] = useState(false);

  const enquiryOnly = detail.unitPrice <= 0 && !(account?.hasTfoAccess && canEditPrice);
  const unitLabel = UNIT_MAP_LONG[unit as keyof typeof UNIT_MAP_LONG];

  const isSelected = React.useMemo(() => {
    if (filteredVariants && filteredVariants.length > 0) {
      // Check if any variant's productId is in the selected productIds
      return filteredVariants.some((variant) => productIds.includes(variant.productId));
    }
    // If no variants, check the parent product's id
    return productIds.includes(productId);
  }, [filteredVariants, productIds, productId]);

  const selectedButtonSx = isSelected
    ? {
        '&:hover': {
          backgroundColor: colors.primary100,
        },
        backgroundColor: colors.primary100,
        color: colors.primary500,
        gap: 1,
        height: 'fit-content',
        alignSelf: 'flex-end',
      }
    : { height: 'fit-content', alignSelf: 'flex-end' };

  const renderFreightCharge = React.useMemo(() => {
    if (isFreightInclusive) {
      return null;
    }

    const freightChargeCannotBeCalculate = freightCharge === null;
    const freightChargeIsCalculated = typeof freightCharge === 'number';

    if (freightChargeIsCalculated) {
      return <ProductPrice label='Freight' price={freightCharge} />;
    }

    if (freightChargeCannotBeCalculate) {
      return (
        <ProductPrice
          label='Freight'
          price={
            <Link onClick={onSelectDistance}>
              <Typography variant='body2' color='primary500'>
                Select Distance
              </Typography>
            </Link>
          }
        />
      );
    }

    return null;
  }, [isFreightInclusive, freightCharge]);

  const renderPrice = React.useMemo(() => {
    if (filteredVariants && filteredVariants.length > 0) {
      // Get all prices from all variants' delivery month plans
      const allPrices = filteredVariants.flatMap((variant) =>
        variant.deliveryMonthPlans.filter((e) => e.unitPrice > 0).map((plan) => plan.unitPrice),
      );

      const lowestPrice = Math.min(...allPrices);
      const highestPrice = Math.max(...allPrices);

      if (lowestPrice !== highestPrice) {
        return (
          <>
            <ProductPrice label='From' price={lowestPrice} unit={unitLabel} />
            <ProductPrice label='to' price={highestPrice} unit={unitLabel} />
          </>
        );
      }
      return <ProductPrice label='Price' price={lowestPrice} unit={unitLabel} />;
    }

    if (variants && variants.length > 0) {
      const allPrices = variants.flatMap((variant) =>
        variant.deliveryMonthPlans.filter((e) => e.unitPrice > 0).map((plan) => plan.unitPrice),
      );

      if (allPrices.length === 0) {
        return <ProductPrice label='Price' price={0} unit={unitLabel} />;
      }

      const lowestPrice = Math.min(...allPrices);
      const highestPrice = Math.max(...allPrices);

      if (lowestPrice !== highestPrice) {
        return (
          <>
            <ProductPrice label='From' price={lowestPrice} unit={unitLabel} />
            <ProductPrice label='to' price={highestPrice} unit={unitLabel} />
          </>
        );
      }
      return <ProductPrice label='Price' price={lowestPrice} unit={unitLabel} />;
    }

    return <ProductPrice label='Price' price={0} unit={unitLabel} />;
  }, [filteredVariants, variants, unitLabel]);

  const selectedVariantsCount = filteredVariants?.filter((variant) =>
    productIds.includes(variant.productId),
  ).length;

  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const branchId = useBranch();
  const { data: productDetails } = useQuery<DetailsApiResponse, AxiosError>({
    queryKey: ['productDetail', productId],
    queryFn: async () => {
      const response = await apiClient.get<DetailsApiResponse, AxiosError>(
        `/products/${productId}?accountNumber=${currentAccount?.accountNumber}&branchId=${branchId}`,
      );
      return response.data;
    },
    enabled: !!productId, // Only run the query if productId is defined
  });

  const imageUrl = generateImageUrl(itemNumber);

  return (
    <Stack direction='column' bgcolor='white' border={'1px solid #D5D6DA'} p={2} gap={1}>
      <Stack direction='row' gap={1}>
        <Box>
          <ImageWithPlaceholder
            height='60px'
            width='60px'
            style={{
              objectFit: 'contain',
            }}
            src={imageUrl}
            alt=''
            placeholderImageUrl={PlaceholderImage}
          />
        </Box>
        <Stack direction='column' gap={1}>
          <Typography
            variant='h4'
            onClick={() => {
              if (productDetails) {
                onClick(productId);
              }
            }}
            sx={productDetails ? { textDecoration: 'underline', cursor: 'pointer' } : {}}
          >
            {description}
          </Typography>
          <Typography variant='caption' color='grey700'>
            SKU {itemNumber}
          </Typography>
        </Stack>
      </Stack>
      <Stack direction={{ sm: 'column', md: 'row' }} gap={1} justifyContent='space-between'>
        <Stack direction='row'>
          {!enquiryOnly && renderPrice}
          {!enquiryOnly && renderFreightCharge}
          {enquiryOnly && (
            <Typography variant='body2' fontWeight={'bold'} color='primary600'>
              Contact your Farmlands Representative
            </Typography>
          )}
        </Stack>
        <Box alignSelf='flex-end'>
          {!enquiryOnly && filteredVariants && filteredVariants?.length < 1 && (
            <Button
              variant={isSelected ? 'contained' : 'outlined'}
              sx={selectedButtonSx}
              onClick={() => onSelect(!isSelected, detail.variants[0])}
            >
              {isSelected ? 'Selected' : 'Select'}
              {isSelected ? <MaterialSymbol>done</MaterialSymbol> : null}
            </Button>
          )}
        </Box>
      </Stack>

      {!enquiryOnly && filteredVariants && filteredVariants.length > 0 && (
        <Stack direction='row' gap='12px' alignItems='center'>
          <Button
            variant='outlined'
            sx={{ height: 'fit-content', alignSelf: 'flex-start' }}
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            View {filteredVariants.length} variants
            <MaterialSymbol
              sx={{
                fontSize: '24px',
                ml: '8px',
                rotate: isExpanded ? '180deg' : '0deg',
                transition: 'rotate 200ms',
              }}
            >
              keyboard_arrow_down
            </MaterialSymbol>
          </Button>
          {selectedVariantsCount > 0 && (
            <Typography variant='body2' color='grey800'>
              {selectedVariantsCount} selected
            </Typography>
          )}
        </Stack>
      )}
      {isExpanded ? (
        filteredVariants.map((prod) => {
          const title =
            prod.sizeDescription && prod.colourDescription
              ? `${prod.sizeDescription} / ${prod.colourDescription}`
              : (prod.colourDescription ?? prod.sizeDescription);
          const isVariantSelected = productIds.includes(prod.productId);

          return (
            <Stack
              key={prod.productId}
              gap={1}
              border={'1px solid #EFEAE6'}
              bgcolor={isVariantSelected ? colors.primary50 : 'white'}
            >
              <Stack p={2}>
                <Typography
                  variant='body2'
                  fontWeight='bold'
                  color='primary500'
                  textTransform={'capitalize'}
                >
                  {title}
                </Typography>
                <Stack direction={'row'} justifyContent={'space-between'}>
                  <ProductPrice
                    label='From'
                    price={Math.min(...prod.deliveryMonthPlans.map((plan) => plan.unitPrice))}
                    unit={unitLabel}
                  />
                  <Button
                    variant={isVariantSelected ? 'contained' : 'outlined'}
                    sx={
                      isVariantSelected
                        ? {
                            '&:hover': {
                              backgroundColor: colors.primary100,
                            },
                            backgroundColor: colors.primary100,
                            color: colors.primary500,
                            gap: 1,
                            height: 'fit-content',
                            alignSelf: 'flex-end',
                          }
                        : { height: 'fit-content', alignSelf: 'flex-end' }
                    }
                    onClick={() => onSelect(!isVariantSelected, prod)}
                  >
                    {isVariantSelected ? 'Selected' : 'Select'}
                    {isVariantSelected ? <MaterialSymbol>done</MaterialSymbol> : null}
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          );
        })
      ) : (
        <></>
      )}
    </Stack>
  );
};

interface ProductPriceProps {
  label: string;
  price: number | React.ReactNode;
  unit?: string;
}

const ProductPrice: React.FC<ProductPriceProps> = ({ label, price, unit }) => {
  return (
    <Stack direction='column' minWidth='160px'>
      <Typography variant='body2' fontWeight='bold'>
        {label}
      </Typography>
      {typeof price === 'number' ? (
        <>
          <Stack direction={'row'} alignItems='end' gap={0.5}>
            <Typography variant='body1' fontWeight='bold' color='secondary700'>
              {formatPrice(price)}
            </Typography>
            {unit && (
              <span>
                <Typography fontSize={'12px'} color='secondary700'>
                  per {unit}
                </Typography>
              </span>
            )}
          </Stack>
          <Typography variant='small' color='secondary700'>
            GST excl.
          </Typography>
        </>
      ) : (
        price
      )}
    </Stack>
  );
};

interface SelectProductViewProps {
  products: SaleAgreementPromoProductSelection[];
  isFreightInclusive: boolean;
  config: SaleAgreementPromo;
  onProductClick: (productId: number) => void;
}

const SelectProductView: React.FC<SelectProductViewProps> = ({
  products,
  isFreightInclusive,
  config,
  onProductClick,
}) => {
  const account = useSelector(selectCurrentFarmerAccount);

  const { state, setState } = useCampaignContext();
  const productIdsSelected = React.useMemo(() => {
    const productIds = state.productSelected.map((p) => p.productId);
    return productIds;
  }, [state.productSelected]);

  function handleOnProductSelect(
    select: boolean,
    product: SalesAgreementPromoProductSelectionVariant,
  ) {
    if (select === false) {
      setState({
        productSelected: state.productSelected.filter((p) => p.productId !== product.productId),
      });
      return;
    }

    // implement when select is true
    const productNotAdded =
      state.productSelected.findIndex((p) => p.productId === product.productId) === -1;

    if (productNotAdded) {
      // pre-select first delivery month plan
      const deliveryMonthPlans = product.deliveryMonthPlans.filter((e) => e.unitPrice > 0);
      if (deliveryMonthPlans.length > 0) {
        const deliveryMonthPlan = deliveryMonthPlans[0];
        product = selectProductDeliveryMonthPlan(product, deliveryMonthPlan);

        setState({
          productSelected: [...state.productSelected, { ...product, originalPrice: product.unitPrice }],
        });
      } else if (account?.hasTfoAccess && config.editPriceAllowed) {
        product = selectProductDeliveryMonthPlan(product, product.deliveryMonthPlans[0]);

        setState({
          productSelected: [...state.productSelected, { ...product, originalPrice: product.unitPrice }],
        });
      }
    }
  }

  function handleOnSelectDistance() {
    setState({ selectDistance: true });
  }
  return (
    <Stack direction='column' gap={2}>
      <TabHeader title='Add products' caption='Select products to include in your agreement' />
      {!config.hideSite ? (
        <SiteSelection
          config={config}
          isFreightInclusive={isFreightInclusive}
          onSelectDistance={handleOnSelectDistance}
        />
      ) : (
        <Stack
          direction='column'
          gap={0.5}
          bgcolor='white'
          border={'1px solid #D5D6DA'}
          px={2}
          py={1.5}
          borderRadius={1}
          display={state.deliveryOption?.type === 'Pickup' ? 'none' : 'block'}
        >
          <Typography variant='caption'>{config.freightNote}</Typography>
        </Stack>
      )}
      {products.map((product) => {
        return (
          <CampaignProduct
            key={product.itemNumber}
            detail={product}
            canEditPrice={config.editPriceAllowed}
            productIds={productIdsSelected}
            onSelect={handleOnProductSelect}
            onSelectDistance={handleOnSelectDistance}
            onClick={onProductClick}
            isFreightInclusive={isFreightInclusive}
            freightCharge={state.freightCharge}
          />
        );
      })}
    </Stack>
  );
};

interface SiteSelectionProps {
  isFreightInclusive: boolean;
  onSelectDistance: () => void;
  config: SaleAgreementPromo;
}

const SiteSelection = ({ isFreightInclusive, onSelectDistance, config }: SiteSelectionProps) => {
  const { state, setState } = useCampaignContext();

  const renderSelectDistance = React.useMemo(() => {
    // freightCharge is not able to calculate
    if (state.freightCharge === null) {
      return (
        <Stack
          direction='row'
          bgcolor={colors.secondary100}
          p={0.5}
          borderRadius='5px'
          width='100%'
          alignItems='center'
          gap={1}
        >
          <MaterialSymbol variant='fill' sx={{ fontSize: '20px', color: colors.secondary900 }}>
            error
          </MaterialSymbol>
          <Typography variant='caption'>
            We're unable to calculate freight cost.&nbsp;
            <Link onClick={onSelectDistance}>Select distance</Link>
          </Typography>
        </Stack>
      );
    }

    // freight is calculated with distance selected
    if (state.freightRangeSelected && typeof state.freightCharge === 'number') {
      return (
        <Stack
          direction='row'
          bgcolor={colors.primary100}
          p={0.5}
          borderRadius='5px'
          width='100%'
          alignItems='center'
          gap={1}
        >
          <MaterialSymbol variant='fill' sx={{ fontSize: '20px', color: colors.primary900 }}>
            error
          </MaterialSymbol>
          <Typography variant='caption'>
            Estimated distance from site&nbsp;
            {`${state.freightRangeSelected.fromKm} - ${state.freightRangeSelected?.toKm} km `}
            <Link onClick={onSelectDistance}>Edit distance</Link>
          </Typography>
        </Stack>
      );
    }

    return null;
  }, [state.freightCharge, state.freightRangeSelected]);

  const renderFreightChartInfo = React.useMemo(() => {
    if (typeof state.freightCharge === 'number') {
      return (
        <Typography variant='caption'>{`Freight ${formatPrice(state.freightCharge)} per sling (GST excl.)`}</Typography>
      );
    }

    return <Typography variant='caption'>{config.freightNote}</Typography>;
  }, [isFreightInclusive, state.freightCharge, config.freightNote]);

  function handleReselectSite() {
    setState({ siteSelected: undefined });
  }

  return (
    <Stack direction='column' gap={1}>
      <Stack
        direction='column'
        gap={0.5}
        bgcolor='white'
        border={'1px solid #D5D6DA'}
        px={2}
        py={1.5}
        borderRadius={1}
      >
        <Stack direction='row' justifyContent='space-between'>
          <Stack direction='row' gap={1}>
            <Typography variant='body2' color='primary500' fontWeight='bold'>
              Site
            </Typography>
            <Typography variant='body2' color='grey800'>
              {state.siteSelected?.siteName}
            </Typography>
          </Stack>

          <ButtonBase
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              alignSelf: 'start',
            }}
            disableRipple
            onClick={handleReselectSite}
          >
            <Typography variant='body2' color='primary500'>
              Edit
            </Typography>
            <MaterialSymbol sx={{ fontSize: '16px', color: colors.primary500 }}>
              edit
            </MaterialSymbol>
          </ButtonBase>
        </Stack>
        {renderFreightChartInfo}
      </Stack>
      {renderSelectDistance}
    </Stack>
  );
};

interface SelectDistanceViewProps {
  freightRanges: FreightRange[];
  onFreightRangeSelect: (value: FreightRange) => void;
}

const SelectDistanceView = ({ freightRanges, onFreightRangeSelect }: SelectDistanceViewProps) => {
  const { state, setState } = useCampaignContext();

  const selectionItems = freightRanges.map((fr, index) => ({
    label: `${fr.fromKm}km - ${fr.toKm}km`,
    value: String(index),
  }));

  const handleFreightRangeChange = (event: SelectChangeEvent<string>) => {
    const index = event.target.value;
    onFreightRangeSelect(freightRanges[Number(index)]);
  };

  return (
    <Stack direction='column' gap={3}>
      <Stack
        direction='row'
        p={1}
        bgcolor={colors.primary100}
        borderRadius={2}
        alignItems='center'
        justifyContent='space-between'
      >
        <Stack direction='column' borderLeft='3px solid #00553D' px={1}>
          <Typography variant='body2' fontWeight='bold' color='primary500'>
            Site
          </Typography>
          <Typography variant='body2' color='primary500'>
            {state.siteSelected?.siteName}
          </Typography>
        </Stack>
        <ButtonBase
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
          }}
          disableRipple
          onClick={() => setState({ siteSelected: undefined })}
        >
          <Typography variant='body2' color='primary500'>
            Edit
          </Typography>
          <MaterialSymbol sx={{ fontSize: '16px', color: colors.primary500 }}>edit</MaterialSymbol>
        </ButtonBase>
      </Stack>
      <Stack gap={0.5}>
        <DropdownList
          label={'Distance band'}
          items={selectionItems}
          placeholder='Join'
          onChange={handleFreightRangeChange}
        />
        <Typography variant='body2' color='grey700'>
          Your freight cost is calculated by the number of slings and distance from your nearest
          site.
        </Typography>
      </Stack>
    </Stack>
  );
};

export default SelectProductTab;
